import { DetailsPanel, LoadingIcon } from '@/components/controls';
import { useNichePersonImage, useSubject, useUserInfo } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
  Folder as FolderIcon,
  KeyboardDoubleArrowDown as KeyboardDoubleArrowDownIcon,
  KeyboardDoubleArrowUp as KeyboardDoubleArrowUpIcon,
  Map as MapIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';
import { Fragment } from 'react';
import { FlagAvatar } from './FlagAvatar';

export function Suspect({ suspect, onOptionClick, onShowMapClick }) {
  const subjectType = 'SUSPECT';
  const {
    query: {
      data: imageSrc,
      isLoading: imageLoading,
      isFetching: imageFetching,
    },
  } = useNichePersonImage(suspect?.personId);
  const {
    query: {
      data: subject,
      isLoading: subjectLoading,
      isFetching: subjectFetching,
    },
  } = useSubject(subjectType, suspect?.id);
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();
  const canEdit = ['ADMIN', 'SUPERVISOR'].includes(briefingRole);

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardHeader
        avatar={<FlagAvatar />}
        action={
          <Fragment>
            <LoadingIcon
              loading={
                subjectLoading ||
                subjectFetching ||
                imageLoading ||
                imageFetching
              }
            />
            {canEdit && (
              <Tooltip
                title={subject?.highPriority ? 'Deprioritise' : 'Prioritise'}
              >
                <IconButton
                  size="small"
                  onClick={onOptionClick(
                    subjectType,
                    suspect?.id,
                    subject?.code,
                    'highPriority',
                  )}
                >
                  {subject?.highPriority ? (
                    <KeyboardDoubleArrowDownIcon sx={{ fontSize: 'inherit' }} />
                  ) : (
                    <KeyboardDoubleArrowUpIcon sx={{ fontSize: 'inherit' }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {suspect?.addresses.length > 0 && (
              <Tooltip title="Show map">
                <IconButton size="small" onClick={onShowMapClick}>
                  <MapIcon sx={{ fontSize: 'inherit' }} />
                </IconButton>
              </Tooltip>
            )}
          </Fragment>
        }
        title={toProperCase(
          [suspect?.forenames, suspect?.surname].filter(Boolean).join(' '),
        )}
      />
      {imageSrc ? (
        <CardMedia
          component="img"
          sx={{ minHeight: 240, width: '100%' }}
          image={imageSrc}
          alt="Photo of suspect"
        />
      ) : (
        <Box
          sx={(theme) => ({
            height: 240,
            width: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
          })}
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {suspect?.occurrenceNumber}
          </Typography>
          <Typography variant="subtitle2">
            {suspect?.time && format(suspect.time, 'dd/MM/yyyy HH:mm')}
          </Typography>
        </Stack>
        {suspect?.wanted && (
          <Stack direction="row" spacing={1} sx={{ overflow: 'scroll' }}>
            <Chip label="Wanted" size="small" />
          </Stack>
        )}
        {suspect?.offence && (
          <Typography variant="body2" sx={{ p: 0.5 }}>
            {suspect.offence}
          </Typography>
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Last victim update',
              value: suspect?.lastVictimUpdatedTime
                ? format(suspect.lastVictimUpdatedTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
            {
              label: 'Last log entry',
              value: suspect?.lastLogEntryTime
                ? format(suspect.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
          ]}
        />
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: suspect?.officerInCase,
            },
            {
              label: 'Team',
              value: suspect?.team,
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Tooltip
            title={
              subject?.pncCirculated ? (
                <Stack>
                  <Typography variant="caption">Circulated on PNC</Typography>
                  <Typography variant="caption">
                    {subject.pncCirculated.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.pncCirculated.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on PNC'
              )
            }
          >
            <IconButton
              size="small"
              onClick={
                canEdit
                  ? onOptionClick(
                      subjectType,
                      suspect?.id,
                      subject?.code,
                      'pncCirculated',
                    )
                  : undefined
              }
              color={subject?.pncCirculated ? 'primary' : 'default'}
            >
              <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.fileReady ? (
                <Stack>
                  <Typography variant="caption">File ready</Typography>
                  <Typography variant="caption">
                    {subject.fileReady.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.fileReady.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'File not ready'
              )
            }
          >
            <IconButton
              size="small"
              onClick={
                canEdit
                  ? onOptionClick(
                      subjectType,
                      suspect?.id,
                      subject?.code,
                      'fileReady',
                    )
                  : undefined
              }
              color={subject?.fileReady ? 'primary' : 'default'}
            >
              <FolderIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.socialMediaCirculated ? (
                <Stack>
                  <Typography variant="caption">
                    Circulated on social media
                  </Typography>
                  <Typography variant="caption">
                    {subject.socialMediaCirculated.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(
                      subject.socialMediaCirculated.time,
                      'dd/MM/yyyy HH:mm',
                    )}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on social media'
              )
            }
          >
            <IconButton
              size="small"
              onClick={
                canEdit
                  ? onOptionClick(
                      subjectType,
                      suspect?.id,
                      subject?.code,
                      'socialMediaCirculated',
                    )
                  : undefined
              }
              color={subject?.socialMediaCirculated ? 'primary' : 'default'}
            >
              <FacebookIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.taser ? (
                <Stack>
                  <Typography variant="caption">Taser officers only</Typography>
                  <Typography variant="caption">
                    {subject.taser.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.taser.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Taser authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={
                canEdit
                  ? onOptionClick(
                      subjectType,
                      suspect?.id,
                      subject?.code,
                      'taser',
                    )
                  : undefined
              }
              color={subject?.taser ? 'primary' : 'default'}
            >
              <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.firearms ? (
                <Stack>
                  <Typography variant="caption">
                    Firearms officers only
                  </Typography>
                  <Typography variant="caption">
                    {subject.firearms.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.firearms.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Firearms authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={
                canEdit
                  ? onOptionClick(
                      subjectType,
                      suspect?.id,
                      subject?.code,
                      'firearms',
                    )
                  : undefined
              }
              color={subject?.firearms ? 'primary' : 'default'}
            >
              <PistolIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
